import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  DollarSign,
  Home,
  Loader2,
  Edit,
  Save,
  Clock,
  Sparkles,
  Target,
  Briefcase,
  User,
  Settings,
  Info,
  ShieldCheck
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPricing = () => {
  const { token } = useAuth();
  const [pricing, setPricing] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Edit Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    price: "",
    duration_minutes: "",
    is_active: true
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchPricing();
  }, [token]);

  const fetchPricing = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/pricing`, { headers });
      setPricing(response.data);
    } catch (error) {
      toast.error("Failed to load pricing");
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (item) => {
    setEditingPricing(item);
    setEditForm({
      name: item.name || "",
      description: item.description || "",
      price: item.price?.toString() || "",
      duration_minutes: item.duration_minutes?.toString() || "",
      is_active: item.is_active !== false
    });
    setEditModalOpen(true);
  };

  const handleSave = async () => {
    if (!editingPricing) return;

    setIsSaving(true);
    try {
      const updateData = {
        name: editForm.name,
        description: editForm.description,
        price: parseFloat(editForm.price) || 0,
        is_active: editForm.is_active
      };

      // Only include duration for consulting_hourly type
      if (editingPricing.service_type === "consulting_hourly" && editForm.duration_minutes) {
        updateData.duration_minutes = parseInt(editForm.duration_minutes) || 60;
      }

      await axios.put(`${API}/admin/pricing/${editingPricing.id}`, updateData, { headers });
      toast.success("Pricing updated successfully");
      setEditModalOpen(false);
      setEditingPricing(null);
      fetchPricing();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update pricing");
    } finally {
      setIsSaving(false);
    }
  };

  const getServiceIcon = (serviceType) => {
    const icons = {
      foundation: Sparkles,
      standard: Target,
      comprehensive: Briefcase,
      consulting_hourly: User
    };
    return icons[serviceType] || DollarSign;
  };

  const getServiceColor = (serviceType) => {
    const colors = {
      foundation: "text-green-400 bg-green-500/20 border-green-500/30",
      standard: "text-blue-400 bg-blue-500/20 border-blue-500/30",
      comprehensive: "text-purple-400 bg-purple-500/20 border-purple-500/30",
      consulting_hourly: "text-amber-400 bg-amber-500/20 border-amber-500/30"
    };
    return colors[serviceType] || "text-slate-400 bg-slate-500/20 border-slate-500/30";
  };

  const formatServiceType = (type) => {
    // Map internal service types to descriptive labels
    // Canonical names come from the service catalog, not here
    const labels = {
      foundation: "Entry Level",
      standard: "Mid-Tier",
      comprehensive: "Full Scope",
      consulting_hourly: "Hourly Consulting"
    };
    return labels[type] || type;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Separate service packages from consulting rates
  const servicePackages = pricing.filter(p => p.service_type !== "consulting_hourly");
  const consultingRates = pricing.filter(p => p.service_type === "consulting_hourly");

  return (
    <div className="min-h-screen bg-background">
      {/* Admin Header */}
      <header className="sticky top-0 z-50 bg-navy-950/95 backdrop-blur border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-slate-400 hover:text-white transition-colors">
              <Home className="w-5 h-5" />
            </Link>
            <h1 className="font-heading text-xl font-bold text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Service Pricing Configuration
            </h1>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <DollarSign className="w-4 h-4" />
            {pricing.length} pricing items
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Internal Configuration Notice */}
        <div className="mb-8 p-4 rounded-xl bg-blue-950/30 border border-blue-500/30" data-testid="pricing-config-notice">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-300 mb-1">Internal Configuration Only</h3>
              <p className="text-blue-300/80 text-sm leading-relaxed">
                These prices are revealed only after a client completes their free evaluation and onboarding process. 
                Changes made here do <strong>not</strong> appear on the public pricing page—the public page shows 
                service descriptions and value messaging without specific prices.
              </p>
            </div>
          </div>
        </div>

        {/* Service Pricing Configuration Section */}
        <div className="mb-8">
          <h2 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-primary" />
            Service Pricing Configuration
          </h2>
          <p className="text-slate-400 text-sm mb-6">
            Configure pricing for your advisory service tiers. These prices are shown to qualified clients 
            after they complete their evaluation and create an account.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {servicePackages.map((item) => {
              const Icon = getServiceIcon(item.service_type);
              const colorClass = getServiceColor(item.service_type);
              return (
                <div
                  key={item.id}
                  className={`glass p-6 rounded-xl border ${item.is_active ? "border-slate-700" : "border-red-500/30 opacity-60"}`}
                  data-testid={`pricing-${item.service_type}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center border ${colorClass}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditModal(item)}
                      className="text-slate-400 hover:text-white"
                      data-testid={`edit-${item.service_type}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>

                  <div className="mb-2">
                    <span className="text-xs text-slate-500 uppercase tracking-wider">
                      {formatServiceType(item.service_type)}
                    </span>
                  </div>

                  <h3 className="font-heading text-lg font-semibold text-white mb-2">
                    {item.name}
                  </h3>

                  <p className="text-slate-400 text-sm mb-4 line-clamp-3">
                    {item.description}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                    <span className="text-2xl font-heading font-bold text-primary">
                      ${item.price?.toLocaleString()}
                    </span>
                    {!item.is_active && (
                      <span className="text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
                        INACTIVE
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Consulting Rates Section */}
        <div>
          <h2 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Personal Consulting Time
          </h2>
          <p className="text-slate-400 text-sm mb-6">
            Set your hourly rate for custom consulting engagements and additional advisory sessions.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {consultingRates.map((item) => {
              const Icon = getServiceIcon(item.service_type);
              const colorClass = getServiceColor(item.service_type);
              return (
                <div
                  key={item.id}
                  className={`glass p-6 rounded-xl border ${item.is_active ? "border-slate-700" : "border-red-500/30 opacity-60"}`}
                  data-testid={`pricing-${item.service_type}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center border ${colorClass}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditModal(item)}
                      className="text-slate-400 hover:text-white"
                      data-testid={`edit-${item.service_type}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>

                  <h3 className="font-heading text-lg font-semibold text-white mb-2">
                    {item.name}
                  </h3>

                  <p className="text-slate-400 text-sm mb-4">
                    {item.description}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                    <div>
                      <span className="text-2xl font-heading font-bold text-primary">
                        ${item.price?.toLocaleString()}
                      </span>
                      <span className="text-slate-400 text-sm ml-1">
                        / {item.duration_minutes || 60} min
                      </span>
                    </div>
                    {!item.is_active && (
                      <span className="text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
                        INACTIVE
                      </span>
                    )}
                  </div>
                </div>
              );
            })}

            {consultingRates.length === 0 && (
              <div className="col-span-2 glass p-8 rounded-xl border border-slate-700 text-center">
                <Clock className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No consulting rates configured yet.</p>
                <p className="text-slate-500 text-sm mt-2">Consulting rates will appear here once pricing is initialized.</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 p-4 rounded-lg bg-slate-800/30 border border-slate-700/50">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
            <p className="text-slate-500 text-xs">
              Pricing configured here is used for post-evaluation service selection and Stripe checkout. 
              The public pricing page displays service descriptions and directs visitors to complete their 
              free evaluation first—no prices are shown publicly.
            </p>
          </div>
        </div>
      </main>

      {/* Edit Pricing Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-lg bg-navy-900 border-slate-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-primary" />
              Edit Pricing Configuration
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Update the pricing details for {editingPricing?.name}. Changes here affect post-evaluation pricing only.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Service Type Badge */}
            {editingPricing && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 uppercase tracking-wider">
                  {formatServiceType(editingPricing.service_type)}
                </span>
              </div>
            )}

            {/* Name */}
            <div className="space-y-2">
              <Label className="text-slate-300">Service Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="bg-navy-950 border-slate-700 text-white"
                data-testid="edit-pricing-name"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="bg-navy-950 border-slate-700 text-white min-h-[100px]"
                data-testid="edit-pricing-description"
              />
            </div>

            {/* Price */}
            <div className="space-y-2">
              <Label className="text-slate-300">Price ($)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editForm.price}
                  onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                  className="bg-navy-950 border-slate-700 text-white pl-10"
                  data-testid="edit-pricing-price"
                />
              </div>
              <p className="text-xs text-slate-500">
                This price is shown to clients after evaluation completion.
              </p>
            </div>

            {/* Duration (only for consulting_hourly) */}
            {editingPricing?.service_type === "consulting_hourly" && (
              <div className="space-y-2">
                <Label className="text-slate-300">Duration (minutes)</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="number"
                    min="15"
                    step="15"
                    value={editForm.duration_minutes}
                    onChange={(e) => setEditForm({ ...editForm, duration_minutes: e.target.value })}
                    className="bg-navy-950 border-slate-700 text-white pl-10"
                    placeholder="60"
                    data-testid="edit-pricing-duration"
                  />
                </div>
              </div>
            )}

            {/* Active Toggle */}
            <div className="flex items-center justify-between py-2">
              <div>
                <Label className="text-slate-300">Active</Label>
                <p className="text-xs text-slate-500">Inactive pricing won't be shown to users</p>
              </div>
              <Switch
                checked={editForm.is_active}
                onCheckedChange={(checked) => setEditForm({ ...editForm, is_active: checked })}
                data-testid="edit-pricing-active"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => setEditModalOpen(false)}
              className="border-slate-700 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-primary hover:bg-primary/90"
              data-testid="save-pricing-btn"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPricing;
